import React, { useEffect, useState } from 'react';
import MemberListModal from './MemberListModal';
import EventNotesPanel from './EventNotesPanel';
import ChatComposer from '../../common/chat/composer/ChatComposer';
import ChatImageModal from '../../common/chat/composer/ChatImageModal';
import ChatDragOverlay from '../../common/chat/composer/ChatDragOverlay';
import ChatEmptyState from '../../common/chat/shared/ChatEmptyState';
import EventMessageRows from '../../common/chat/timeline/EventMessageRows';
import { AdminEventChatGuidanceMessage } from '../../common/chat/timeline/ArtistChatGuidanceSystemMessages';
import ChatFollowupInbox from '../../common/chat/followups/ChatFollowupInbox';
import ChatAnnouncementBar from '../../common/chat/announcements/ChatAnnouncementBar';
import ChatAnnouncementListModal from '../../common/chat/announcements/ChatAnnouncementListModal';
import ChatMessageReactionBar from '../../common/chat/reactions/ChatMessageReactionBar';
import ChatMessageMenuReactionPicker from '../../common/chat/reactions/ChatMessageMenuReactionPicker';
import ChatMessageReactionDetailModal from '../../common/chat/reactions/ChatMessageReactionDetailModal';
import ChatMessageReadStatusDetailModal from '../../common/chat/reactions/ChatMessageReadStatusDetailModal';
import ChatReplySnippet from '../../common/chat/composer/ChatReplySnippet';
import ChatProfileModalLayer from '../../common/chat/members/ChatProfileModalLayer';
import { formatDateTimeJST } from '../../../utils/datetime';
import { supportsChatMessageReaction } from '../../common/chat/reactions/chatMessageReactionUtils';
import { shouldExcludeAdminSenderForMessage } from '../../common/chat/timeline/adminSenderExclusionUtils';
import { linkifyTextNodes as linkifyText } from '../../common/chat/timeline/linkifyTextNodes';
import { pushViewState } from './eventChatPageHelpers';

export default function EventChatConversation({
  isAiTargetSelected,
  isResolvingInitialChatTarget = false,
  followupSummary,
  followupRefreshKey,
  handleOpenAiTask,
  goToEventList,
  isNarrow,
  mobileView,
  selectedEventId,
  unreadDividerMessageId = '',
  latestAnnouncement,
  announcementCount,
  setIsAnnouncementListOpen,
  showNotes,
  showDelayedInitialLoading,
  isUploading,
  showNewMsgBanner,
  bannerMsg,
  handleBannerClick,
  canViewEventNotes,
  refreshNoteCount,
  canManageEventNotes,
  canEditMaterials,
  canDownloadMaterials,
  loggedInAdminId,
  saveLast,
  loadingOlderMessages,
  hasMore,
  isDragActive,
  overlayRect,
  displayMessages,
  activeAnchorMessageId,
  renderAnnouncementAvatarLeft,
  isAnnouncementMessage,
  isMyAdminMessage,
  shouldSubtractSelf,
  getMainPartLabel,
  getAnnouncementSenderName,
  renderAdminAvatarLeft,
  renderArtistAvatarLeft,
  handleAvatarClick,
  inlineCopyMessageId,
  handleMessageContextMenu,
  handleMessageTouchStart,
  clearMessageLongPress,
  handleJumpToReplyMessage,
  renderPartialCopyContent,
  renderMessageContent,
  toggleReaction,
  openReactionDetail,
  isReactionPending,
  resolveMessageSourceBadgeLabel,
  shouldShowMessageSourceBadge,
  withAnnouncementScopeTitle,
  shouldShowChatGuidance,
  imgModalOpen,
  imageItems,
  imgModalIndex,
  closeImgModal,
  showPrevImg,
  showNextImg,
  messageMenu,
  closeMessageMenu,
  messageMenuRef,
  canReactMessage,
  handleMessageMenuReactionSelect,
  canReplyMessage,
  handleSelectReplyMessage,
  canCopyMessage,
  handleCopyMessage,
  handleOpenInlinePartialCopy,
  canAnnouncementMessage,
  announcementActionMessageId,
  isAnnouncedMessage,
  handleToggleAnnouncementMessage,
  canInspectReadStatus,
  handleOpenReadStatusDetail,
  canUnsendMessage,
  handleUnsendMessage,
  handleUploading,
  replyDraft,
  setReplyDraft,
  mentionCandidates,
  handleBeforeEventChatSend,
  isReadOnly,
  toastError,
  updateSidebarAfterSend,
  fetchNewMessages,
  refreshPendingMentionTargets,
  success,
  promptSelfAssignmentAfterSend,
  chatContainerRef,
  showMembersModal,
  setShowMembersModal,
  selectedEventAdmins,
  conciergePlanner,
  hasConciergeParticipants,
  isSelectedGroupUnified,
  selectedEventInfo,
  effectiveGroupEvents,
  setSelectedEventInfo,
  setGroupPanelEvents,
  profileTarget,
  closeProfileModal,
  isAnnouncementListOpen,
  announcements,
  unpinAnnouncement,
  handleSelectAnnouncementMessage,
  isReactionDetailOpen,
  reactionDetailLoading,
  reactionDetailGroups,
  closeReactionDetail,
  isReadStatusDetailOpen,
  readStatusDetailLoading,
  readStatusDetailReaders,
  closeReadStatusDetail,
}) {
  const [failedBannerAvatarUrl, setFailedBannerAvatarUrl] = useState('');
  useEffect(() => {
    setFailedBannerAvatarUrl('');
  }, [bannerMsg?.avatarUrl]);

  if (isResolvingInitialChatTarget) {
    return (
      <div className="chat-empty-state-shell" aria-label="チャットを開いています">
        <div className="chat-spinner" aria-hidden="true"></div>
      </div>
    );
  }

  if (isAiTargetSelected) {
    return (
      <ChatFollowupInbox
        initialSummary={followupSummary}
        refreshKey={followupRefreshKey}
        onOpenTask={handleOpenAiTask}
        onBack={goToEventList}
        showBackButton={Boolean(isNarrow && mobileView === 'chat')}
      />
    );
  }

  if (!selectedEventId) {
    return <ChatEmptyState ariaLabel="イベントチャットが未選択です" />;
  }

  return (
    <>
      {!showNotes ? (
        <ChatAnnouncementBar
          announcement={latestAnnouncement}
          count={announcementCount}
          onOpenList={() => setIsAnnouncementListOpen(true)}
        />
      ) : null}

      {((showDelayedInitialLoading && !shouldShowChatGuidance) || isUploading) && (
        <div className="chat-loading-overlay" aria-label={isUploading ? '送信中' : '読み込み中'}>
          <div className="chat-spinner" aria-hidden="true"></div>
        </div>
      )}

      {!showNotes && showNewMsgBanner && bannerMsg && (
        <div className="event-chat-new-msg-banner" onClick={handleBannerClick}>
          {bannerMsg.avatarUrl && failedBannerAvatarUrl !== bannerMsg.avatarUrl && (
            <img
              src={bannerMsg.avatarUrl}
              alt="avatar"
              className="event-chat-banner-avatar"
              onError={() => setFailedBannerAvatarUrl(bannerMsg.avatarUrl)}
            />
          )}
          <div className="event-chat-banner-text">
            <div className="event-chat-banner-name">
              {bannerMsg.name}
              {bannerMsg.part && (<span className="event-chat-banner-part">（{bannerMsg.part}）</span>)}
            </div>
            <div className="event-chat-banner-preview">{bannerMsg.preview}</div>
          </div>
        </div>
      )}

      {showNotes && canViewEventNotes ? (
        <EventNotesPanel
          eventId={selectedEventId}
          onModeChange={(mode) => {
            if (!selectedEventId) return;
            if (mode === 'list' || mode === 'create') {
              pushViewState(selectedEventId, 'notes-list');
              saveLast(selectedEventId, 'notes-list');
            } else if (mode?.type === 'detail') {
              pushViewState(selectedEventId, 'notes-detail');
              saveLast(selectedEventId, 'notes-list');
            } else if (mode?.type === 'imgmodal') {
              pushViewState(selectedEventId, 'notes-image');
              saveLast(selectedEventId, 'notes-list');
            } else if (mode?.type === 'edit') {
              pushViewState(selectedEventId, 'notes-detail');
              saveLast(selectedEventId, 'notes-list');
            }
          }}
          onNotesChanged={refreshNoteCount}
          canCreateNote={canManageEventNotes}
          canManageNotes={canManageEventNotes}
          canEditMaterials={canEditMaterials}
          canDownloadMaterials={canDownloadMaterials}
          adminUserId={loggedInAdminId}
        />
      ) : (
        <>
          <div
            className="chat-messages admin-message-chat-messages chat-message-stack"
            ref={(element) => { chatContainerRef.current = element; }}
          >
            <ChatDragOverlay
              show={!!isDragActive}
              rect={overlayRect}
              title="ここにドロップで送信"
              sub="画像・PDF・Excel・Word・テキスト（100MBまで）"
            />

            <EventMessageRows
              messages={displayMessages}
              unreadDividerMessageId={unreadDividerMessageId}
              activeAnchorMessageId={activeAnchorMessageId}
              cancelledRowProps={{
                rowClassName: 'im-row system',
                bodyClassName: 'im-col',
                contentRowClassName: 'im-line',
                sourceBadgeClassName: 'im-source-event-badge',
                timeClassName: 'im-time',
                renderAnnouncementAvatarLeft,
              }}
              buildStandardRowProps={(msg) => ({
                isAnnouncement: isAnnouncementMessage(msg),
                isMyMessage: isMyAdminMessage(msg),
                hideSenderMeta: shouldExcludeAdminSenderForMessage(msg),
                shouldSubtractSelfRead: shouldSubtractSelf,
                getMainPartLabel,
                getAnnouncementSenderName,
                senderNameFallback: 'アーティスト',
                bubbleClassByType: {
                  announcement: 'admin-message note-card',
                  self: 'artist-message',
                  other: 'admin-message',
                },
                useFullWidthPartParentheses: true,
                variant: 'im',
                sourceBadgeClassName: 'im-source-event-badge',
                renderAnnouncementAvatarLeft,
                renderAdminAvatarLeft,
                renderArtistAvatarLeft,
                onAvatarClick:
                  !shouldExcludeAdminSenderForMessage(msg) && !isAnnouncementMessage(msg)
                    ? handleAvatarClick
                    : undefined,
                onContextMenu: inlineCopyMessageId ? undefined : (event) => handleMessageContextMenu(event, msg),
                onTouchStart: inlineCopyMessageId ? undefined : (event) => handleMessageTouchStart(event, msg),
                onTouchEnd: inlineCopyMessageId ? undefined : clearMessageLongPress,
                onTouchCancel: inlineCopyMessageId ? undefined : clearMessageLongPress,
                onTouchMove: inlineCopyMessageId ? undefined : clearMessageLongPress,
                replyHeaderContent: msg.reply ? (
                  <ChatReplySnippet
                    reply={msg.reply}
                    onClick={() => handleJumpToReplyMessage(msg.reply)}
                  />
                ) : null,
                rowClassName:
                  inlineCopyMessageId && inlineCopyMessageId === String(msg?.id || '').trim()
                    ? 'chat-message-row--copyActive'
                    : inlineCopyMessageId && inlineCopyMessageId !== String(msg?.id || '').trim()
                      ? 'chat-message-row--copyDimmed'
                      : '',
                bubbleClassName:
                  inlineCopyMessageId && inlineCopyMessageId === String(msg?.id || '').trim()
                    ? 'chat-message-row__bubbleWrap--copyActive'
                    : '',
                renderMessageContent: (message) =>
                  renderPartialCopyContent(message, renderMessageContent(message), isMyAdminMessage(message)),
                sideActionContent:
                  !inlineCopyMessageId &&
                  !isAnnouncementMessage(msg) &&
                  !isMyAdminMessage(msg) &&
                  supportsChatMessageReaction(msg) ? (
                    <ChatMessageReactionBar
                      messageId={msg.id}
                      reactions={msg.reactions}
                      canReact
                      showReactionChips={false}
                      onToggleReaction={toggleReaction}
                      onOpenDetail={openReactionDetail}
                      isPending={isReactionPending}
                      compact
                    />
                  ) : null,
                metaEndContent:
                  !isAnnouncementMessage(msg) &&
                  Array.isArray(msg.reactions) &&
                  msg.reactions.length > 0 ? (
                    <ChatMessageReactionBar
                      messageId={msg.id}
                      reactions={msg.reactions}
                      canReact={false}
                      onOpenDetail={openReactionDetail}
                      isPending={isReactionPending}
                      showAddButton={false}
                    />
                  ) : null,
              })}
              resolveMessageSourceBadgeLabel={resolveMessageSourceBadgeLabel}
              shouldShowMessageSourceBadge={shouldShowMessageSourceBadge}
              formatDateTime={formatDateTimeJST}
              withAnnouncementScopeTitle={withAnnouncementScopeTitle}
              linkifyText={linkifyText}
            />
            {loadingOlderMessages && hasMore && (
              <div className="chat-paging-loading" aria-label="追加読み込み中">
                <div className="chat-spinner chat-spinner--small" aria-hidden="true"></div>
              </div>
            )}
            {shouldShowChatGuidance ? <AdminEventChatGuidanceMessage /> : null}
          </div>

          <ChatImageModal
            isOpen={imgModalOpen && imageItems.length > 0}
            src={imageItems[imgModalIndex]?.fullSrc}
            downloadItems={imageItems}
            currentIndex={imgModalIndex}
            onClose={() => closeImgModal(false)}
            onPrev={showPrevImg}
            onNext={showNextImg}
            showNav={imageItems.length > 1}
          />

          {messageMenu && (
            <div className="message-menu-overlay" onClick={closeMessageMenu}>
              <div
                ref={messageMenuRef}
                className="message-menu"
                style={{ top: messageMenu.y, left: messageMenu.x }}
                onClick={(event) => event.stopPropagation()}
              >
                {canReactMessage(messageMenu.message) ? (
                  <ChatMessageMenuReactionPicker
                    messageId={messageMenu.message?.id}
                    reactions={messageMenu.message?.reactions}
                    onSelectReaction={handleMessageMenuReactionSelect}
                    isPending={isReactionPending}
                  />
                ) : null}
                {canReplyMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleSelectReplyMessage}
                  >
                    <i className="fa-solid fa-reply" aria-hidden="true" />
                    <span>返信する</span>
                  </button>
                ) : null}
                {canCopyMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleCopyMessage}
                  >
                    <i className="fa-solid fa-copy" aria-hidden="true" />
                    <span>コピー</span>
                  </button>
                ) : null}
                {canCopyMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleOpenInlinePartialCopy}
                  >
                    <i className="fa-solid fa-i-cursor" aria-hidden="true" />
                    <span>一部をコピー</span>
                  </button>
                ) : null}
                {canAnnouncementMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleToggleAnnouncementMessage}
                  >
                    <i
                      className={`fa-solid ${
                        announcementActionMessageId === String(messageMenu.message?.id || '')
                          ? 'fa-spinner fa-spin'
                          : isAnnouncedMessage(messageMenu.message?.id)
                            ? 'fa-bullhorn-slash'
                            : 'fa-bullhorn'
                      }`}
                      aria-hidden="true"
                    />
                    <span>
                      {announcementActionMessageId === String(messageMenu.message?.id || '')
                        ? '処理中…'
                        : isAnnouncedMessage(messageMenu.message?.id)
                          ? 'アナウンス解除'
                          : 'アナウンスに設定'}
                    </span>
                  </button>
                ) : null}
                {canInspectReadStatus(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleOpenReadStatusDetail}
                  >
                    <i className="fa-solid fa-check-double" aria-hidden="true" />
                    <span>既読を確認</span>
                  </button>
                ) : null}
                {canUnsendMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item message-menu__item--danger"
                    onClick={handleUnsendMessage}
                  >
                    送信取り消し
                  </button>
                ) : null}
              </div>
            </div>
          )}

          <div
            className="chat-composer-dock"
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--color-surface)',
              zIndex: 'var(--z-raised)',
            }}
          >
            <ChatComposer
              mode="admin"
              scope="event"
              eventId={selectedEventId}
              canSendFiles
              senderType="admin"
              senderId={loggedInAdminId}
              placeholderText="メッセージを入力…"
              onUploading={handleUploading}
              replyDraft={replyDraft}
              onReplyCancel={() => setReplyDraft(null)}
              onReplyChange={setReplyDraft}
              mentionCandidates={mentionCandidates}
              onBeforeSend={handleBeforeEventChatSend}
              readOnly={isReadOnly}
              onSendBlocked={(message) => {
                if (toastError) toastError(message || '現在メンテナンス中のため送信できません。');
              }}
              onSent={async (summary) => {
                if (isReadOnly) {
                  toastError && toastError('現在メンテナンス中のため送信できません。');
                  return;
                }
                const preview =
                  summary?.type === 'images' ? '画像が届いています。' :
                  summary?.type === 'file' ? 'ファイルが届いています。' :
                  (summary?.text || '');
                if (preview) updateSidebarAfterSend(preview);

                await fetchNewMessages();
                refreshPendingMentionTargets();
                setReplyDraft(null);
                success('メッセージを送信しました');
                await promptSelfAssignmentAfterSend();
                requestAnimationFrame(() => {
                  if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                  }
                });
              }}
            />
          </div>
        </>
      )}

      {showMembersModal && (
        <MemberListModal
          eventId={selectedEventId}
          open={showMembersModal}
          onClose={() => setShowMembersModal(false)}
          assignedAdmins={selectedEventAdmins}
          conciergePlanner={conciergePlanner}
          isConciergeEvent={hasConciergeParticipants}
          isUnifiedEventGroupChat={isSelectedGroupUnified}
          currentEvent={selectedEventInfo}
          groupEventRows={effectiveGroupEvents}
          setCurrentEventState={setSelectedEventInfo}
          setGroupEventsState={setGroupPanelEvents}
        />
      )}

      <ChatProfileModalLayer
        profileTarget={profileTarget}
        onClose={closeProfileModal}
      />

      <ChatAnnouncementListModal
        open={isAnnouncementListOpen}
        announcements={announcements}
        onClose={() => setIsAnnouncementListOpen(false)}
        onRemove={unpinAnnouncement}
        removingMessageId={announcementActionMessageId}
        onSelect={handleSelectAnnouncementMessage}
      />

      <ChatMessageReactionDetailModal
        open={isReactionDetailOpen}
        loading={reactionDetailLoading}
        groups={reactionDetailGroups}
        onClose={closeReactionDetail}
      />
      <ChatMessageReadStatusDetailModal
        open={isReadStatusDetailOpen}
        loading={readStatusDetailLoading}
        readers={readStatusDetailReaders}
        onClose={closeReadStatusDetail}
      />
    </>
  );
}
